/**
 * Storage, addressing and validation rules for resume profile photos.
 *
 * URL durability is the reason this module exists. `personalInfo.profilePictureUrl`
 * is not a transient browser-side value: it is re-rendered by the PDF Lambda,
 * which holds no session, and by public share links, whose viewers hold no
 * session either. A pre-signed S3 URL expires after an hour, so storing one
 * would render correctly right after upload and then silently break every
 * resume that used it. We therefore store a *stable* URL addressing our own
 * serving route, which streams the object out of S3 on each request:
 *
 *   https://<app-origin>/api/resume-photos/<userId>/<photoId>.<ext>
 *   -> s3://<bucket>/resume-photos/<userId>/<photoId>.<ext>
 *
 * The route is deliberately unauthenticated — a share-link viewer has no
 * session and the Lambda cannot present one — and is guarded instead by the
 * unguessable random `photoId`, the same trade-off a public-read S3 object
 * under a random key would make. Nothing about the object's ACL changes, so
 * this needs no bucket-policy or Block-Public-Access change to ship.
 */

/** S3 "folder" holding every profile photo, kept apart from resume source files. */
export const RESUME_PHOTO_S3_PREFIX = 'resume-photos';

/** Path prefix of the stable serving route. Mirrors the S3 prefix on purpose. */
export const RESUME_PHOTO_PUBLIC_PATH_PREFIX = '/api/resume-photos';

/**
 * Upload ceiling. The client crops to a square canvas before uploading, so a
 * finished photo is tens of kilobytes; this bounds the *original* a user picks.
 */
export const MAX_PROFILE_PHOTO_BYTES = 8 * 1024 * 1024;

/**
 * Raster formats only. SVG is excluded deliberately: it is executable markup,
 * and we serve photos from our own origin, so an uploaded SVG would be a
 * stored-XSS vector against the app.
 */
export const ACCEPTED_PROFILE_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export type ProfilePhotoMimeType = (typeof ACCEPTED_PROFILE_PHOTO_MIME_TYPES)[number];

export const ACCEPTED_PROFILE_PHOTO_FILE_EXTENSIONS = '.jpg,.jpeg,.png,.webp' as const;

export const PROFILE_PHOTO_FORMATS_HUMAN_LABEL = 'JPEG, PNG, or WebP';
export const PROFILE_PHOTO_MAX_SIZE_HUMAN_LABEL = `max ${Math.round(
  MAX_PROFILE_PHOTO_BYTES / (1024 * 1024)
)}MB`;

export type ProfilePhotoExtension = 'jpg' | 'png' | 'webp';

const EXTENSION_BY_MIME: Record<ProfilePhotoMimeType, ProfilePhotoExtension> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const MIME_BY_EXTENSION: Record<string, ProfilePhotoMimeType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

/** Random v4 id minted per upload; replacing a photo always mints a new one. */
const PHOTO_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Conservative: our user ids are cuids, but this also blocks `..` path games. */
const USER_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function extensionForProfilePhotoMime(mimeType: string): ProfilePhotoExtension | null {
  return EXTENSION_BY_MIME[mimeType as ProfilePhotoMimeType] ?? null;
}

export function profilePhotoMimeForExtension(extension: string): ProfilePhotoMimeType | null {
  return MIME_BY_EXTENSION[extension.toLowerCase()] ?? null;
}

function readAscii(bytes: Uint8Array, start: number, end: number): string {
  let out = '';
  for (let index = start; index < end; index += 1) {
    out += String.fromCharCode(bytes[index]);
  }
  return out;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Identify the format from the bytes themselves.
 *
 * The browser-supplied `Content-Type` is attacker-controlled, so it is never
 * what we act on: an SVG (or anything else) announced as `image/png` fails to
 * match any signature here and is rejected.
 */
export function sniffProfilePhotoMimeType(bytes: Uint8Array): ProfilePhotoMimeType | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }

  if (bytes.length >= 8 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)) {
    return 'image/png';
  }

  if (bytes.length >= 12 && readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 12) === 'WEBP') {
    return 'image/webp';
  }

  return null;
}

export type ProfilePhotoRejectionReason = 'empty' | 'too-large' | 'wrong-type';

export type ProfilePhotoValidation =
  | { ok: true; mimeType: ProfilePhotoMimeType }
  | { ok: false; reason: ProfilePhotoRejectionReason; message: string };

const TOO_LARGE_MESSAGE = `Image must be smaller than ${PROFILE_PHOTO_MAX_SIZE_HUMAN_LABEL.replace('max ', '')}`;
const WRONG_TYPE_MESSAGE = `Only ${PROFILE_PHOTO_FORMATS_HUMAN_LABEL} images are allowed`;

/** Server-side gate: size plus a real signature check on the received bytes. */
export function validateProfilePhotoBytes(bytes: Uint8Array): ProfilePhotoValidation {
  if (bytes.length === 0) {
    return { ok: false, reason: 'empty', message: 'Image file is empty' };
  }
  if (bytes.length > MAX_PROFILE_PHOTO_BYTES) {
    return { ok: false, reason: 'too-large', message: TOO_LARGE_MESSAGE };
  }

  const mimeType = sniffProfilePhotoMimeType(bytes);
  if (!mimeType) {
    return { ok: false, reason: 'wrong-type', message: WRONG_TYPE_MESSAGE };
  }

  return { ok: true, mimeType };
}

/**
 * Client-side pre-check so the picker can reject an obviously wrong file
 * without a round trip. Advisory only — the server re-checks the bytes.
 */
export function validateProfilePhotoFile(file: { size: number; type: string }): ProfilePhotoValidation {
  if (file.size === 0) {
    return { ok: false, reason: 'empty', message: 'Image file is empty' };
  }
  if (file.size > MAX_PROFILE_PHOTO_BYTES) {
    return { ok: false, reason: 'too-large', message: TOO_LARGE_MESSAGE };
  }

  const mimeType = ACCEPTED_PROFILE_PHOTO_MIME_TYPES.find((accepted) => accepted === file.type);
  if (!mimeType) {
    return { ok: false, reason: 'wrong-type', message: WRONG_TYPE_MESSAGE };
  }

  return { ok: true, mimeType };
}

export interface ProfilePhotoLocation {
  userId: string;
  photoId: string;
  extension: ProfilePhotoExtension;
}

export function buildProfilePhotoS3Key(location: ProfilePhotoLocation): string {
  const { userId, photoId, extension } = location;
  return `${RESUME_PHOTO_S3_PREFIX}/${userId}/${photoId}.${extension}`;
}

export function buildProfilePhotoPath(location: ProfilePhotoLocation): string {
  const { userId, photoId, extension } = location;
  return `${RESUME_PHOTO_PUBLIC_PATH_PREFIX}/${userId}/${photoId}.${extension}`;
}

/**
 * The value stored in `personalInfo.profilePictureUrl`.
 *
 * Absolute, because the PDF Lambda renders the HTML with no document origin to
 * resolve a relative path against.
 */
export function buildProfilePhotoUrl(baseUrl: string, location: ProfilePhotoLocation): string {
  return `${baseUrl.replace(/\/+$/, '')}${buildProfilePhotoPath(location)}`;
}

/**
 * Recognise one of our own photo URLs and recover its S3 coordinates.
 *
 * Host-insensitive on purpose: it is what lets us still delete (and inline)
 * photos stored under a previous deployment origin. Anything else — an
 * arbitrary URL a user typed into the old text input, an external avatar —
 * returns `null` and is left strictly alone.
 */
export function parseProfilePhotoUrl(url: unknown): ProfilePhotoLocation | null {
  if (typeof url !== 'string' || !url.trim()) return null;

  let pathname: string;
  try {
    // Relative values resolve against a throwaway origin; only the path matters.
    pathname = new URL(url.trim(), 'https://profile-photo.invalid').pathname;
  } catch {
    return null;
  }

  const prefix = `${RESUME_PHOTO_PUBLIC_PATH_PREFIX}/`;
  if (!pathname.startsWith(prefix)) return null;

  const segments = pathname.slice(prefix.length).split('/');
  if (segments.length !== 2) return null;

  try {
    return parseProfilePhotoSegments(decodeURIComponent(segments[0]), decodeURIComponent(segments[1]));
  } catch {
    return null;
  }
}

/**
 * Validate the `<userId>/<photoId>.<ext>` pair straight off the serving route's
 * params. Shares every rule with `parseProfilePhotoUrl`, which delegates here,
 * so a path that route accepts is exactly a path an upload could have minted.
 */
export function parseProfilePhotoSegments(userId: string, fileName: string): ProfilePhotoLocation | null {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) return null;

  const photoId = fileName.slice(0, dotIndex);
  const extension = fileName.slice(dotIndex + 1).toLowerCase();

  if (!USER_ID_PATTERN.test(userId)) return null;
  if (!PHOTO_ID_PATTERN.test(photoId)) return null;
  if (extension !== 'jpg' && extension !== 'png' && extension !== 'webp') return null;

  return { userId, photoId, extension };
}

/**
 * Whether a stored value is safe to drop into an `<img src>` in resume HTML.
 *
 * Existing resumes may hold any URL a user once typed, so this stays permissive
 * about the host — it only bars schemes that would execute (`javascript:`,
 * `vbscript:`) or reach the renderer's own filesystem (`file:`). `data:image/…`
 * is allowed because the PDF path inlines our photos as data URIs.
 */
export function isRenderableProfilePictureUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;

  const value = url.trim();
  if (!value) return false;

  if (/^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(value)) return true;

  // Same-origin path (never stored today, but harmless and unambiguous).
  if (value.startsWith('/') && !value.startsWith('//')) return true;

  try {
    const protocol = new URL(value).protocol.toLowerCase();
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}
